import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

export interface ICaseNotificationItem {
  caseId: string;
  isUnsigned: boolean;
  isActive: boolean;
  isRead: boolean;
  userId?: string; // Optional: undefined for unassigned cases, userId for assigned cases
}

interface CaseNotificationStore {
  caseNotifications: ICaseNotificationItem[];

  // Actions
  actions: {
    addCaseNotification: (caseNotification: ICaseNotificationItem) => void;
    removeCaseNotificationByCaseId: (caseId: string) => void;
    clearCaseNotifications: () => void;
    clearActiveCaseNotifications: (userId: string) => void;
    clearUnsignedCaseNotifications: (userId: string) => void;
  };
}

export const caseNotificationStore = create<CaseNotificationStore>((set, get) => ({
  caseNotifications: [],

  actions: {
    addCaseNotification: (caseNotification: ICaseNotificationItem) => {
      const state = get();

      // Check for duplicate notifications (same caseId and userId)
      const isDuplicate = state.caseNotifications.some(
        (notification) => notification.caseId === caseNotification.caseId
      );

      // Don't add if it's a duplicate
      if (isDuplicate) {
        return;
      }

      set((state) => ({ caseNotifications: [...state.caseNotifications, caseNotification] }));
    },

    /**
     * Remove all notifications for a specific caseId (regardless of userId)
     */
    removeCaseNotificationByCaseId: (caseId: string) => {
      if (!caseId) return;

      set((state) => {
        const filtered = state.caseNotifications.filter((notification) => notification.caseId !== caseId);
        return { caseNotifications: filtered };
      });
    },

    /**
     * Clear all case notifications
     */
    clearCaseNotifications: () => {
      set({ caseNotifications: [] });
    },

    /**
     * Clear all active case notifications for a specific user
     */
    clearActiveCaseNotifications: (userId: string) => {
      if (!userId) return;

      set((state) => {
        const filtered = state.caseNotifications.filter(
          (notification) => !(notification.userId === userId && notification.isActive)
        );
        return { caseNotifications: filtered };
      });
    },

    /**
     * Clear all unassigned case notifications for a specific user
     */
    clearUnsignedCaseNotifications: (userId: string) => {
      if (!userId) return;

      set((state) => {
        const filtered = state.caseNotifications.filter(
          (notification) => !(notification.userId === userId && notification.isUnsigned)
        );
        return { caseNotifications: filtered };
      });
    },
  },
}));

// selectors

/**
 * Check if user has any active case notifications
 */
export const useCheckHasActiveCaseNotification = (userId: string) =>
  caseNotificationStore(
    useShallow((state) => {
      if (!userId) return false;
      const { caseNotifications } = state;
      return caseNotifications.some((notification) => notification.userId === userId && notification.isActive);
    })
  );

/**
 * Check if there are any unassigned case notifications
 * Unassigned cases have userId === undefined
 */
export const useCheckHasUnsignedCaseNotification = (userId: string) =>
  caseNotificationStore(
    useShallow((state) => {
      if (!userId) return false;
      const { caseNotifications } = state;
      return caseNotifications.some((notification) => notification.userId === userId && notification.isUnsigned);
    })
  );

/**
 * Check if a specific case has a notification for the user
 * Works for both unassigned cases and user's assigned cases
 */
export const useCheckHasCaseNotification = (userId: string, caseId: string) =>
  caseNotificationStore(
    useShallow((state) => {
      if (!userId || !caseId) return false;
      const { caseNotifications } = state;
      return caseNotifications.some((notification) => notification.caseId === caseId && notification.userId === userId);
    })
  );

// actions
export const useCaseNotificationActionsStore = () => caseNotificationStore(useShallow((state) => state.actions));
